/* eslint-disable react/no-array-index-key */
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IconType } from 'react-icons';
import { TbHelp } from 'react-icons/tb';

import { useSettings } from '@cccom/shared/settings';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@cccom/ui/src/components/shadcn/tooltip';
import { cn } from '@cccom/ui/src/utils';

function IconCircle({
  icon: IconComp,
  color = 'text-black/70',
  className,
  ...props
}: {
  icon: IconType;
  color?: string;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full bg-white text-2xl',
        color,
        className
      )}
      {...props}
    >
      <IconComp />
    </div>
  );
}

function HelpLink({
  url,
  children,
  className,
  ...props
}: {
  url: string;
  children: ReactNode;
  className?: string;
} & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a
      className={cn('flex', className)}
      target="_blank"
      rel="noopener noreferrer"
      href={url}
      {...props}
    >
      {children}
    </a>
  );
}

export type HelpCardProps = { isCollapsed: boolean };

export function HelpCard({ isCollapsed }: HelpCardProps) {
  const [t] = useTranslation();
  const { helpCenterUrl } = useSettings();

  return (
    <HelpLink
      url={helpCenterUrl}
      className="transition-colors hover:bg-gray-100"
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'flex w-full items-center gap-4 px-7 py-4 text-gray-500',
              isCollapsed ? 'justify-center' : 'justify-start'
            )}
          >
            <IconCircle icon={TbHelp} color="text-gray-500 bg-transparent" />
            <span
              className={cn(
                'whitespace-nowrap',
                isCollapsed ? 'absolute opacity-0' : 'relative opacity-100'
              )}
            >
              {t('support.help_center')}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="text-xs">
          {t('support.help_center')}
        </TooltipContent>
      </Tooltip>
    </HelpLink>
  );
}
